import React, { useState } from 'react';

const TemplateCategoryList = ({ templates, selectedTemplate, onTemplateSelect, isLoading }) => {
  const [activeCategory, setActiveCategory] = useState('configuration');
  const [expandedCategories, setExpandedCategories] = useState({
    configuration: true,
    account: false
  });

  // Template categories as specified
  const templateCategories = {
    configuration: {
      name: 'Configuration',
      templates: [
        { id: 'send-test-email', name: 'Send Test Email', type: 'configuration' },
        { id: 'proposal', name: 'Proposal', type: 'configuration' },
        // ✅ NEW: template for proposal approval-request emails
        { id: 'proposal_approval', name: 'Proposal Approval Request', type: 'configuration' },
        // ✅ NEW: template for task assignment emails
        { id: 'task', name: 'Task Assignment', type: 'configuration' },
        // ✅ NEW: templates for task reminder emails
        { id: 'task_reminder_today', name: 'Task Reminder (Due Today)', type: 'configuration' },
        { id: 'task_reminder_tomorrow', name: 'Task Reminder (Due Tomorrow)', type: 'configuration' },
        { id: 'invoice_overdue_reminder', name: 'Invoice Overdue (Daily, Lead Assignee)', type: 'configuration' },
        // ✅ NEW: template for lead assignment emails
        { id: 'lead', name: 'Lead Assignment', type: 'configuration' },
        { id: 'invoice', name: 'Invoice', type: 'configuration' }
      ]
    },
    account: {
      name: 'Account',
      templates: [
        { id: 'reset-password', name: 'Reset Password', type: 'account' },
        { id: 'email-verification', name: 'Email Verification', type: 'account' },
        { id: 'change-email-request', name: 'Change Email Request', type: 'account' },
        { id: 'account-deletion-confirmation', name: 'Account Deletion Confirmation', type: 'account' }
      ]
    }
  };

  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
    setActiveCategory(category);
  };

  const handleTemplateClick = (template) => {
    console.log('=== DEBUG: TemplateCategoryList handleTemplateClick ===');
    console.log('Template clicked:', template);
    
    // Find existing template or create new one
    const existingTemplate = templates.find(t => t.templateId === template.id);
    console.log('Existing template found:', existingTemplate);
    
    if (existingTemplate) {
      console.log('Using existing template:', existingTemplate);
      // Mark as not new for existing templates
      existingTemplate.isNew = false;
      onTemplateSelect(existingTemplate);
    } else {
      // Create new template with default content
      const newTemplate = {
        templateId: template.id, // This should be 'proposal'
        name: template.name,
        type: template.type,
        subject: getDefaultSubject(template.id),
        content: getDefaultContent(template.id),
        isNew: true // Set isNew flag explicitly
      };
      
      console.log('=== NEW TEMPLATE CREATED ===');
      console.log('New template object:', newTemplate);
      console.log('New template.templateId:', newTemplate.templateId);
      console.log('New template.isNew:', newTemplate.isNew);
      console.log('=== END NEW TEMPLATE ===');
      
      onTemplateSelect(newTemplate);
    }
  };

  const getDefaultSubject = (templateId) => {
    const subjects = {
      'send-test-email': 'Test Email from Your Platform',
      'proposal': 'Proposal for Your Project',
      // ✅ Default subject for approval-request emails
      'proposal_approval': 'Approval needed for proposal {{proposal_code}}',
      // ✅ Default subject for task assignment emails
      'task': 'New Task Assigned: {{task_title}}',
      // ✅ Default subjects for task reminder emails
      'task_reminder_today': '⏰ URGENT: Task Due TODAY - {{task_title}}',
      'task_reminder_tomorrow': '📅 Task Due Tomorrow - {{task_title}}',
      'invoice_overdue_reminder': '📋 Overdue invoice(s) on your leads — {{invoice_count}} ({{current_date}})',
      // ✅ Default subject for lead assignment emails
      'lead': 'New Lead Assigned: {{project_name}}',
      'invoice': 'Invoice Request: {{invoice_number}}',
      'reset-password': 'Password Reset Request',
      'email-verification': 'Verify Your Email Address',
      'change-email-request': 'Email Change Request',
      'account-deletion-confirmation': 'Account Deletion Confirmation'
    };
    return subjects[templateId] || 'Email Template';
  };

  const getDefaultContent = (templateId) => {
    const contents = {
      'send-test-email': `
        <h2>Test Email</h2>
        <p>Hello {{user_name}},</p>
        <p>This is a test email to verify your email configuration is working correctly.</p>
        <p>If you received this email, your email settings are properly configured.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      'proposal': `
        <h2>Proposal for {{project_name}}</h2>
        <p>Dear {{client_name}},</p>
        <p>Thank you for considering our services for your project. We are pleased to present our proposal for {{project_name}}.</p>
        <p><strong>Project Overview:</strong></p>
        <p>{{project_description}}</p>
        <p><strong>Our Approach:</strong></p>
        <p>{{approach_details}}</p>
        <p><strong>Timeline:</strong></p>
        <p>{{timeline_details}}</p>
        <p><strong>Investment:</strong></p>
        <p>{{pricing_details}}</p>
        <p>We look forward to discussing this proposal with you and answering any questions you may have.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      // ✅ Default content for proposal approval-request emails
      'proposal_approval': `
        <h2>Approval Needed for Proposal {{proposal_code}}</h2>
        <p>Dear {{approver_name}},</p>
        <p>A proposal requires your approval.</p>
        <p><strong>Proposal Code:</strong> {{proposal_code}}</p>
        <p><strong>Client:</strong> {{company_name}}</p>
        <p><strong>Status:</strong> {{status_desc}}</p>
        {{#if note}}<p><strong>Note from requester:</strong> {{note}}</p>{{/if}}
        <p>This proposal was submitted by {{user_name}} and is waiting for your review in the system.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      // ✅ Default content for task assignment emails
      'task': `
        <h2>New Task Assignment</h2>
        <p>Dear {{assigned_user}},</p>
        <p>You have been assigned a new task. Here are the details:</p>
        <table style="border-collapse: collapse; width: 100%; margin: 12px 0;">
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 140px;">Task Title</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{task_title}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Type</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{task_type}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Priority</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{priority}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Status</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{status}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Due Date</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{due_date}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Client</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{client_name}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Project</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{project_name}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Contact</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{contact_name}}</td>
          </tr>
        </table>
        <p><strong>Description:</strong></p>
        <p>{{description}}</p>
        <p style="margin-top: 16px;">Assigned by {{created_by}} on {{created_at}}.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      // ✅ Default content for task reminder emails (Due Today)
      'task_reminder_today': `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>Task Due Today - Urgent Reminder</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5; }
                .container { max-width: 600px; margin: 0 auto; background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
                .header { background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); color: white; padding: 20px; border-radius: 8px 8px 0 0; text-align: center; }
                .content { padding: 20px; }
                .task-details { background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 15px 0; border-left: 4px solid #dc3545; }
                .task-field { margin: 8px 0; }
                .task-label { font-weight: bold; color: #495057; }
                .urgent { color: #dc3545; font-weight: bold; font-size: 1.1em; }
                .footer { text-align: center; color: #6c757d; font-size: 12px; margin-top: 20px; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h2>🚨 URGENT: Task Due TODAY!</h2>
                    <p class="urgent">{{urgency_message}}</p>
                </div>
                <div class="content">
                    <p>Dear <strong>{{assigned_user}}</strong>,</p>

                    <p><strong class="urgent">This task is due TODAY and requires immediate attention!</strong></p>

                    <div class="task-details">
                        <div class="task-field">
                            <span class="task-label">Task Title:</span> <strong>{{task_title}}</strong>
                        </div>
                        <div class="task-field">
                            <span class="task-label">Type:</span> {{task_type}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Priority:</span> {{priority}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Status:</span> {{status}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Due Date:</span> <span class="urgent">{{due_date}}</span>
                        </div>
                        <div class="task-field">
                            <span class="task-label">Client:</span> {{client_name}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Project:</span> {{project_name}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Contact:</span> {{contact_name}}
                        </div>
                        {{description}}
                    </div>

                    <p class="urgent"><strong>Please take immediate action to complete this task or update its status if it's already been completed.</strong></p>

                    <p>You can access the task in the CRM system to view more details and update its progress.</p>

                    <p>Best regards,<br>CRM System</p>
                </div>
                <div class="footer">
                    <p>This is an automated urgent reminder from the CRM Task Management System.</p>
                </div>
            </div>
        </body>
        </html>
      `,
      // ✅ Default content for task reminder emails (Due Tomorrow)
      'task_reminder_tomorrow': `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>Task Due Tomorrow - Reminder</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5; }
                .container { max-width: 600px; margin: 0 auto; background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
                .header { background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%); color: #212529; padding: 20px; border-radius: 8px 8px 0 0; text-align: center; }
                .content { padding: 20px; }
                .task-details { background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 15px 0; border-left: 4px solid #ffc107; }
                .task-field { margin: 8px 0; }
                .task-label { font-weight: bold; color: #495057; }
                .warning { color: #856404; font-weight: bold; }
                .footer { text-align: center; color: #6c757d; font-size: 12px; margin-top: 20px; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h2>📅 Task Due Tomorrow</h2>
                    <p class="warning">{{urgency_message}}</p>
                </div>
                <div class="content">
                    <p>Dear <strong>{{assigned_user}}</strong>,</p>

                    <p>This is a friendly reminder that you have a task due tomorrow:</p>

                    <div class="task-details">
                        <div class="task-field">
                            <span class="task-label">Task Title:</span> {{task_title}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Type:</span> {{task_type}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Priority:</span> {{priority}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Status:</span> {{status}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Due Date:</span> {{due_date}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Client:</span> {{client_name}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Project:</span> {{project_name}}
                        </div>
                        <div class="task-field">
                            <span class="task-label">Contact:</span> {{contact_name}}
                        </div>
                        {{description}}
                    </div>

                    <p>Please plan to complete this task by tomorrow to avoid any delays.</p>

                    <p>You can access the task in the CRM system to view more details and update its progress.</p>

                    <p>Best regards,<br>CRM System</p>
                </div>
                <div class="footer">
                    <p>This is an automated reminder from the CRM Task Management System.</p>
                </div>
            </div>
        </body>
        </html>
      `,
      // ✅ Default content for lead assignment emails
      'lead': `
        <h2>New Lead Assignment</h2>
        <p>Dear {{assigned_user}},</p>
        <p>You have been assigned a new lead. Here are the details:</p>
        <table style="border-collapse: collapse; width: 100%; margin: 12px 0;">
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 140px;">Project Name</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{project_name}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Company Name</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{company_name}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Contact Name</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{contact_name}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Email</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{email}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Stage</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{stage}}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Sector</td>
            <td style="padding: 8px; border: 1px solid #ddd;">{{sector}}</td>
          </tr>
        </table>
        <p><strong>Project Description:</strong></p>
        <p>{{project_description}}</p>
        <p style="margin-top: 16px;">Assigned by {{created_by}} on {{created_at}}.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      'invoice_overdue_reminder': `
        <!DOCTYPE html>
        <html><head><meta charset="UTF-8"/><title>Overdue invoices</title></head>
        <body style="font-family:Arial,sans-serif;line-height:1.5;color:#333;">
          <p>Dear <strong>{{assigned_user}}</strong>,</p>
          <p>The following invoice(s) linked to <strong>leads assigned to you</strong> are <strong>past their due date</strong> (as of {{current_date}}).</p>
          <p>Count: <strong>{{invoice_count}}</strong></p>
          {{invoice_table}}
          <p style="margin-top:20px;font-size:13px;color:#666;">This is an automated daily reminder. Please follow up on payment or update the invoice status in the CRM.</p>
        </body></html>
      `,
      'invoice': `
        <p>Dear Finance Team,</p>
        <p>We would like to request the issuance of an invoice with the following details:</p>
        <p><strong>Client Name</strong>: {{client_name}}</p>
        <p><strong>Proposal No.</strong>: {{proposal_code}}</p>
        <p><strong>Service Description</strong>: {{description}}</p>
        <p><strong>Invoice Amount</strong>: {{amount_due}}</p>
        <p><strong>Due Date</strong>: {{due_date}}</p>
        <p><strong>Message</strong>: {{invoice_notes}}</p>
        <p>Attached are the supporting documents for invoice issuance:</p>
        <ul>
          <li>Approved proposal / contract</li>
        </ul>
        <p>Please proceed with the invoice issuance and let us know once the invoice has been generated.</p>
        <p>Thank you for your support.</p>
        <p>Best regards,<br>{{assigned_user_name}}</p>
      `,
      'reset-password': `
        <h2>Password Reset Request</h2>
        <p>Hello {{user_name}},</p>
        <p>We received a request to reset your password. Click the link below to reset it:</p>
        <p><a href="{{reset_link}}" style="background-color: #007bff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Reset Password</a></p>
        <p>If you didn't request this, please ignore this email.</p>
        <p>This link will expire in {{expiry_time}}.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      'email-verification': `
        <h2>Verify Your Email Address</h2>
        <p>Hello {{user_name}},</p>
        <p>Please verify your email address by clicking the link below:</p>
        <p><a href="{{verification_link}}" style="background-color: #28a745; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Verify Email</a></p>
        <p>If you didn't create an account, please ignore this email.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      'change-email-request': `
        <h2>Email Change Request</h2>
        <p>Hello {{user_name}},</p>
        <p>We received a request to change your email address to {{new_email}}.</p>
        <p>Click the link below to confirm this change:</p>
        <p><a href="{{confirmation_link}}" style="background-color: #ffc107; color: #212529; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm Email Change</a></p>
        <p>If you didn't request this change, please contact support immediately.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `,
      'account-deletion-confirmation': `
        <h2>Account Deletion Confirmation</h2>
        <p>Hello {{user_name}},</p>
        <p>We received a request to delete your account. This action cannot be undone.</p>
        <p>Click the link below to confirm account deletion:</p>
        <p><a href="{{deletion_link}}" style="background-color: #dc3545; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm Account Deletion</a></p>
        <p>If you didn't request this, please contact support immediately.</p>
        <p>Best regards,<br>{{company_name}}</p>
      `
    };
    return contents[templateId] || '<p>Email template content</p>';
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Template Categories</h3>
        </div>
        <div className="card-body">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Template Categories</h3>
      </div>
      <div className="card-body p-0">
        <div className="accordion" id="templateCategoriesAccordion">
          {Object.keys(templateCategories).map((category, index) => (
            <div className="accordion-item" key={category}>
              <h2 className="accordion-header" id={`heading-${category}`}>
                <button
                  className={`accordion-button ${!expandedCategories[category] ? 'collapsed' : ''}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse-${category}`}
                  aria-expanded={expandedCategories[category]}
                  aria-controls={`collapse-${category}`}
                  onClick={() => handleCategoryToggle(category)}
                >
                  {templateCategories[category].name}
                </button>
              </h2>
              <div
                id={`collapse-${category}`}
                className={`accordion-collapse collapse ${expandedCategories[category] ? 'show' : ''}`}
                aria-labelledby={`heading-${category}`}
                data-bs-parent="#templateCategoriesAccordion"
              >
                <div className="accordion-body p-0">
                  <div className="list-group list-group-flush">
                    {templateCategories[category].templates.map((template) => {
                      const isSelected = selectedTemplate && selectedTemplate.templateId === template.id;
                      
                      return (
                        <div
                          key={template.id}
                          className={`list-group-item list-group-item-action ${
                            isSelected ? 'active' : ''
                          }`}
                          onClick={() => handleTemplateClick(template)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="fw-bold" style={{ fontSize: '0.875rem' }}>{template.name}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemplateCategoryList; 